/**
 * TipTap Custom Node: AnnotationLink
 *
 * Rendert einen inline Chip/Badge, der auf eine Annotation verweist.
 * Beim Scrollen des Dokuments wird die verknüpfte Annotation im 3D-Viewer angesteuert.
 *
 * Syntax im Dokument:
 * { type: 'annotationLink', attrs: { annotationId: 'abc-123', label: 'Stütze A1' } }
 */

import { Node, mergeAttributes } from '@tiptap/core'

export interface AnnotationLinkOptions {
  /**
   * Callback: wird aufgerufen, wenn der Benutzer auf das Chip klickt.
   * Ermöglicht die Kameranavigation vom Dokumenteditor aus.
   */
  onAnnotationClick?: (annotationId: string) => void

  /**
   * Gibt die Farbe für einen Annotationstyp zurück.
   * Wird aus dem Store gelesen, wenn verfügbar.
   */
  getAnnotationColor?: (annotationId: string) => string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    annotationLink: {
      /**
       * Fügt einen AnnotationLink an der aktuellen Cursorposition ein.
       */
      insertAnnotationLink: (attrs: { annotationId: string; label: string }) => ReturnType
    }
  }
}

export const AnnotationLinkNode = Node.create<AnnotationLinkOptions>({
  name: 'annotationLink',
  group: 'inline',
  inline: true,
  atom: true,
  // draggable wird NICHT gesetzt — eigenes Drag-System in EmbedDragPlugin.ts

  addOptions() {
    return {
      onAnnotationClick: undefined,
      getAnnotationColor: undefined,
    }
  },

  addAttributes() {
    return {
      annotationId: {
        default: null,
        parseHTML: el => el.getAttribute('data-annotation-id'),
        renderHTML: attrs => ({ 'data-annotation-id': attrs.annotationId }),
      },
      label: {
        default: 'Annotation',
        parseHTML: el => el.getAttribute('data-label'),
        renderHTML: attrs => ({ 'data-label': attrs.label }),
      },
    }
  },

  parseHTML() {
    return [
      { tag: 'span[data-type="annotation-link"]' },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'annotation-link',
        class: 'annotation-link-chip',
        contenteditable: 'false',
      }),
      HTMLAttributes['data-label'] ?? 'Annotation',
    ]
  },

  addCommands() {
    return {
      insertAnnotationLink:
        attrs =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          })
        },
    }
  },

  // Node views werden in React via @tiptap/react's ReactNodeViewRenderer implementiert.
  // Siehe AnnotationLinkView.tsx
})
